// Self-test page (E-08) and spike harness (S-01, S-02, S-03, S-04, S-08). Runs entirely locally.

import { SourceClient } from '../media/source-client';
import { readBarcode } from './barcode';
import type { DecodedFrame } from '../media/protocol';
import { GLRenderer } from '../render/gl-renderer';
import { resolveColor } from '../core/color';
import { benchDecode } from './bench';
import { colorCheck } from './color-check';

export interface FileReport {
  file: string;
  ok: boolean;
  error?: string;
  spec?: unknown;
  keyFrames?: number;
  openMs?: number;
  firstFrameMs?: number;
  sequential?: { frames: number; mismatches: number; firstMismatch?: string; fps: number };
  seek?: { count: number; mismatches: number; avgMs: number; maxMs: number; p95Ms: number; firstMismatch?: string };
  reverse?: { frames: number; mismatches: number; fps: number };
  formats?: string[];
  baseCode?: number;
  bytesPerFrame?: number;
  uploadMs?: number;
  frameErrors?: { index: number; detail: string }[];
}

function waitFrame(c: SourceClient, index: number, timeoutMs = 20000): Promise<DecodedFrame | null> {
  return new Promise((resolve) => {
    const existing = c.getFrame(index);
    if (existing) return resolve(existing);
    const t = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const off = c.addFrameListener((i: number) => {
      if (i === index) {
        cleanup();
        resolve(c.getFrame(index) ?? null);
      }
    });
    const cleanup = () => {
      clearTimeout(t);
      off();
    };
  });
}

function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 2 ** 32;
  };
}

export interface TestOptions {
  seeks?: number;
  reverseFrames?: number;
  preferSoftware?: boolean;
  /** Expected barcode for display index i; default: baseCode + i where baseCode is read from frame 0. */
  expected?: (i: number, base: number) => number;
  sequential?: boolean;
}

export async function testFile(file: File, opts: TestOptions = {}, renderer?: GLRenderer): Promise<FileReport> {
  const rep: FileReport = { file: file.name, ok: false };
  const c = new SourceClient(file);
  c.budgetBytes = 1.5 * 1024 * 1024 * 1024;
  const expected = opts.expected ?? ((i: number, base: number) => base + i);
  const formats = new Set<string>();
  try {
    const t0 = performance.now();
    const info = await c.open(opts.preferSoftware);
    rep.openMs = Math.round(performance.now() - t0);
    rep.spec = info.spec;
    rep.keyFrames = info.keyFrames.length;
    const n = info.spec.frameCount;
    const t1 = performance.now();
    c.request([0]);
    const f0 = await waitFrame(c, 0);
    rep.firstFrameMs = Math.round(performance.now() - t1);
    if (!f0) throw new Error('first frame not decoded');
    const base = readBarcode(f0);
    rep.baseCode = base;
    rep.bytesPerFrame = f0.data.byteLength;
    if (renderer) {
      const tu = performance.now();
      for (let k = 0; k < 10; k++) renderer.uploadFrame('T', f0, resolveColor(info.spec), `t${k}`);
      renderer.gl.finish();
      rep.uploadMs = Math.round(((performance.now() - tu) / 10) * 100) / 100;
    }

    // Sequential forward decode of every frame (S-01 item 2/3).
    if (opts.sequential !== false) {
      let mism = 0;
      let firstMismatch: string | undefined;
      const ts = performance.now();
      for (let i = 0; i < n; i++) {
        const win: number[] = [];
        for (let k = i; k < Math.min(n, i + 8); k++) win.push(k);
        c.request(win, [i]);
        const f = await waitFrame(c, i);
        if (!f) {
          mism++;
          firstMismatch ??= `frame ${i}: not decoded (${c.frameErrors.get(i) ?? 'timeout'})`;
          continue;
        }
        if (f.sourceFormat) formats.add(f.sourceFormat);
        const code = readBarcode(f);
        if (code !== expected(i, base)) {
          mism++;
          firstMismatch ??= `frame ${i}: barcode ${code}, expected ${expected(i, base)}`;
        }
      }
      rep.sequential = { frames: n, mismatches: mism, firstMismatch, fps: Math.round((n / (performance.now() - ts)) * 1000 * 10) / 10 };
    }

    // Random seeks (S-01 item 2 and 4): each seek is a cold request for one frame.
    const seeks = opts.seeks ?? 200;
    if (seeks > 0) {
      const r = rng(1234);
      const times: number[] = [];
      let mism = 0;
      let firstMismatch: string | undefined;
      for (let s = 0; s < seeks; s++) {
        const idx = Math.floor(r() * n);
        c.clearCache();
        const tq = performance.now();
        c.request([idx], [idx]);
        const f = await waitFrame(c, idx);
        times.push(performance.now() - tq);
        if (!f) {
          mism++;
          firstMismatch ??= `seek ${idx}: not decoded`;
          continue;
        }
        const code = readBarcode(f);
        if (code !== expected(idx, base)) {
          mism++;
          firstMismatch ??= `seek ${idx}: barcode ${code}, expected ${expected(idx, base)}`;
        }
      }
      const sorted = [...times].sort((a, b) => a - b);
      rep.seek = {
        count: seeks,
        mismatches: mism,
        avgMs: Math.round(times.reduce((a, b) => a + b, 0) / times.length),
        maxMs: Math.round(sorted[sorted.length - 1]),
        p95Ms: Math.round(sorted[Math.floor(sorted.length * 0.95)]),
        firstMismatch,
      };
    }

    // Reverse playback (S-01 item 6): step backwards, requesting the whole GOP window behind the playhead.
    const rev = Math.min(opts.reverseFrames ?? 72, n);
    if (rev > 0) {
      const keys = info.keyFrames;
      let mism = 0;
      const tr = performance.now();
      for (let i = n - 1; i >= n - rev; i--) {
        let k = 0;
        for (const kf of keys) if (kf <= i) k = kf;
        const win: number[] = [i];
        for (let j = i - 1; j >= k; j--) win.push(j);
        c.request(win, win);
        const f = await waitFrame(c, i);
        if (!f || readBarcode(f) !== expected(i, base)) mism++;
      }
      rep.reverse = { frames: rev, mismatches: mism, fps: Math.round((rev / (performance.now() - tr)) * 1000 * 10) / 10 };
    }
    rep.formats = [...formats];
    rep.frameErrors = c.errorLog.map((e) => ({ index: e.index, detail: e.detail }));
    rep.ok =
      (rep.sequential?.mismatches ?? 0) === 0 && (rep.seek?.mismatches ?? 0) === 0 && (rep.reverse?.mismatches ?? 0) === 0;
  } catch (e) {
    const err = e as { errorKey?: string; detail?: string };
    rep.error = err.errorKey ? `${err.errorKey}: ${err.detail}` : String(e);
  } finally {
    c.dispose();
  }
  return rep;
}

/** S-04: decode several sources in parallel and measure sustained throughput. */
export async function testParallel(files: File[], frames = 120): Promise<{ sources: number; frames: number; fps: number; ok: boolean }> {
  const clients = files.map((f) => new SourceClient(f));
  try {
    await Promise.all(clients.map((c) => c.open()));
    const t0 = performance.now();
    let ok = true;
    for (let i = 0; i < frames; i++) {
      const got = await Promise.all(
        clients.map((c) => {
          const win: number[] = [];
          for (let k = i; k < Math.min(c.frameCount, i + 8); k++) win.push(k);
          c.request(win, [i]);
          return waitFrame(c, i);
        }),
      );
      for (const f of got) if (!f || readBarcode(f) !== i) ok = false;
    }
    const fps = (frames / (performance.now() - t0)) * 1000;
    return { sources: files.length, frames, fps: Math.round(fps * 10) / 10, ok };
  } finally {
    clients.forEach((c) => c.dispose());
  }
}

declare global {
  interface Window {
    __selftest?: {
      testFile: typeof testFile;
      testParallel: typeof testParallel;
      renderer: GLRenderer | null;
      results: FileReport[];
      setDebug: (v: boolean) => void;
      benchDecode: typeof benchDecode;
      colorCheck: typeof colorCheck;
    };
  }
}

const out = document.getElementById('out') as HTMLPreElement;
const summary = document.getElementById('summary') as HTMLTableElement;
let renderer: GLRenderer | null = null;
try {
  renderer = new GLRenderer(document.getElementById('gl') as HTMLCanvasElement);
} catch {
  renderer = null;
}
const results: FileReport[] = [];
window.__selftest = { testFile, testParallel, renderer, results, setDebug: (v: boolean) => (SourceClient.debug = v), benchDecode, colorCheck };

document.getElementById('run')!.addEventListener('click', async () => {
  const input = document.getElementById('files') as HTMLInputElement;
  const soft = (document.getElementById('soft') as HTMLInputElement).checked;
  summary.innerHTML = '<tr><th>檔案 File</th><th>結果 Result</th><th>逐格 Seq</th><th>定位 Seek (avg/p95/max ms)</th><th>倒退 Reverse fps</th><th>格式 Format</th></tr>';
  results.length = 0;
  for (const f of Array.from(input.files ?? [])) {
    out.textContent += `Testing ${f.name}…\n`;
    const r = await testFile(f, { preferSoftware: soft }, renderer ?? undefined);
    results.push(r);
    const tr = document.createElement('tr');
    const cell = (t: string, cls = '') => {
      const td = document.createElement('td');
      td.textContent = t;
      if (cls) td.className = cls;
      tr.appendChild(td);
    };
    cell(f.name);
    cell(r.ok ? 'OK' : `FAIL ${r.error ?? ''}`, r.ok ? 'ok' : 'bad');
    cell(r.sequential ? `${r.sequential.frames - r.sequential.mismatches}/${r.sequential.frames} @ ${r.sequential.fps} fps` : '—');
    cell(r.seek ? `${r.seek.avgMs} / ${r.seek.p95Ms} / ${r.seek.maxMs}` : '—');
    cell(r.reverse ? `${r.reverse.fps}` : '—');
    cell((r.formats ?? []).join(', '));
    summary.appendChild(tr);
    out.textContent += JSON.stringify(r, null, 2) + '\n';
  }
});
